
import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Rating, TextField, Button, Typography, Box, IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { apiRequest } from "../../fetchSetup";
import useOrderFeedbackPrompt from "../../hooks/useOrderFeedbackPrompt";

const REVIEWS_API = process.env.REACT_APP_REVIEWS_API;

const TYPE_META = {
  medicine:   { badge: "💊 MEDICINE ORDER",       label: "medicine order"                },
  diagnostic: { badge: "🧪 DIAGNOSTIC BOOKING",   label: "diagnostic booking"            },
  nursing:    { badge: "🏥 NURSING / PHYSIO",      label: "nursing/physiotherapy service" },
  package:    { badge: "📦 HEALTH PACKAGE",        label: "health package"                },
};

// All feedback now goes through the reviews blueprint so every type
const getEndpoint = (type, referenceId) => {
  switch (type) {
    case "medicine":
      // Use reviews blueprint — supports feedbacks array
      return `${REVIEWS_API}/submit-medicine-feedback/${referenceId}`;
    case "diagnostic":
      return `${REVIEWS_API}/submit-diagnostic-feedback/${referenceId}`;
    case "nursing":
      return `${REVIEWS_API}/submit-nursing-feedback/${referenceId}`;
    case "package":
      return `${REVIEWS_API}/submit-package-feedback/${referenceId}`;
    default:
      return `${REVIEWS_API}/submit-diagnostic-feedback/${referenceId}`;
  }
};

const OrderFeedbackPrompt = ({ user }) => {
  const {
    pendingReview, showFlash,
    dismissFlash, markSubmitted,
  } = useOrderFeedbackPrompt(user);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [rating,     setRating]     = useState(0);
  const [comment,    setComment]    = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");

  const popupOpen = showFlash && !!pendingReview;
  if (!pendingReview) return null;

  const meta = TYPE_META[pendingReview.type] || TYPE_META.medicine;

  const openRatingDialog = () => { dismissFlash(); setDialogOpen(true); };

  const closeDialog = () => {
    setDialogOpen(false);
    setRating(0);
    setComment("");
    setError("");
  };

  const submitFeedback = async () => {
    if (!rating) { setError("Please select a star rating"); return; }
    setSubmitting(true);
    setError("");
    try {
      const endpoint = getEndpoint(pendingReview.type, pendingReview.reference_id);
      const res = await apiRequest(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Failed to submit feedback");
      closeDialog();
      markSubmitted();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {popupOpen && (
        <div className="ofp-overlay">
          <div className="ofp-card">
            <button className="ofp-close" onClick={dismissFlash}>✕</button>
            <div className="ofp-glow" />
            <div className="ofp-content">
              <div className="ofp-badge">{meta.badge}</div>
              <h2 className="ofp-title">How was your experience?</h2>
              <p className="ofp-sub">
                Your {meta.label} is complete!<br />
                <span style={{ fontSize: 13, opacity: 0.85 }}>{pendingReview.title}</span>
              </p>
              <div className="ofp-actions">
                <button className="ofp-btn-primary" onClick={openRatingDialog}>
                  Give Feedback
                </button>
                <button className="ofp-btn-secondary" onClick={dismissFlash}>
                  Maybe Later
                </button>
              </div>
            </div>
          </div>

          <style>{`
            .ofp-overlay {
              position: fixed; inset: 0;
              background: rgba(0,0,0,0.55);
              backdrop-filter: blur(6px);
              z-index: 99999;
              display: flex; align-items: center; justify-content: center;
              padding: 20px;
              animation: ofpFadeIn .25s ease;
            }
            .ofp-card {
              position: relative; width: 100%; max-width: 400px;
              overflow: hidden; border-radius: 28px;
              background: linear-gradient(135deg, rgba(0,109,119,0.96) 0%, rgba(0,169,181,0.88) 100%);
              border: 1px solid rgba(255,255,255,0.15);
              box-shadow: 0 20px 60px rgba(0,0,0,0.35);
            }
            .ofp-glow {
              position: absolute; width: 220px; height: 220px;
              border-radius: 50%; background: rgba(255,255,255,0.12);
              filter: blur(60px); top: -70px; right: -50px;
            }
            .ofp-content {
              position: relative; padding: 34px 28px;
              text-align: center; z-index: 2;
            }
            .ofp-close {
              position: absolute; top: 14px; right: 14px;
              width: 36px; height: 36px; border: none; border-radius: 50%;
              background: rgba(255,255,255,0.14); color: #fff;
              font-size: 16px; cursor: pointer; z-index: 5;
            }
            .ofp-close:hover { background: rgba(255,255,255,0.22); }
            .ofp-badge {
              display: inline-flex; align-items: center; gap: 8px;
              padding: 8px 16px; border-radius: 999px;
              background: rgba(255,255,255,0.14);
              border: 1px solid rgba(255,255,255,0.18);
              color: #CFFAFE; font-size: 12px; font-weight: 700;
              margin-bottom: 18px;
            }
            .ofp-title {
              color: #fff; font-size: 22px; line-height: 1.2;
              margin: 0; font-weight: 800;
            }
            .ofp-sub {
              margin-top: 12px; color: rgba(255,255,255,0.85);
              font-size: 14px; line-height: 1.6;
            }
            .ofp-actions {
              margin-top: 24px; display: flex;
              flex-direction: column; gap: 10px;
            }
            .ofp-btn-primary {
              padding: 12px 24px; border: none; border-radius: 14px;
              background: #fff; color: #006D77;
              font-size: 15px; font-weight: 700; cursor: pointer;
              transition: all .2s ease;
            }
            .ofp-btn-primary:hover { transform: translateY(-2px); opacity: 0.92; }
            .ofp-btn-secondary {
              padding: 10px 24px;
              border: 1px solid rgba(255,255,255,0.3); border-radius: 14px;
              background: transparent; color: rgba(255,255,255,0.8);
              font-size: 14px; font-weight: 600; cursor: pointer;
            }
            .ofp-btn-secondary:hover { background: rgba(255,255,255,0.08); }
            @keyframes ofpFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @media (max-width: 480px) {
              .ofp-content { padding: 28px 20px; }
              .ofp-title { font-size: 19px; }
            }
          `}</style>
        </div>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          fontWeight: 700, color: "#006D77",
          display: "flex", justifyContent: "space-between", alignItems: "center"
        }}>
          Rate Your Experience
          <IconButton onClick={closeDialog} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 0.5, fontWeight: 600, color: "#006D77", fontSize: 13 }}>
            {meta.badge}
          </Typography>
          <Typography sx={{ mb: 2, color: "text.secondary" }}>
            {pendingReview.title}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Rating size="large" value={rating} onChange={(e, val) => setRating(val)} />
          </Box>
          <TextField
            fullWidth multiline rows={3}
            placeholder="Tell us more about your experience (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {error && (
            <Typography color="error" fontSize={13} sx={{ mt: 1 }}>{error}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} disabled={submitting} sx={{ textTransform: "none" }}>
            Maybe Later
          </Button>
          <Button
            variant="contained" onClick={submitFeedback} disabled={submitting}
            sx={{ bgcolor: "#006D77", textTransform: "none", "&:hover": { bgcolor: "#004F56" } }}
          >
            {submitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderFeedbackPrompt;